import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { detectLang } from "@/utils/detectLang";
import { T } from "@/utils/landingTranslations";

import LandingNav from "@/components/landing/LandingNav";
import HeroSection from "@/components/landing/HeroSection";
import BenefitsSection from "@/components/landing/BenefitsSection";
import HowItWorksSection from "@/components/landing/HowItWorksSection";
import FeaturesSection from "@/components/landing/FeaturesSection";
import AISection from "@/components/landing/AISection";
import PillarsSection from "@/components/landing/PillarsSection";
import ResultsSection from "@/components/landing/ResultsSection";
import IntegrationsSection from "@/components/landing/IntegrationsSection";
import AllInOneSection from "@/components/landing/AllInOneSection";
import PricingSection from "@/components/landing/PricingSection";
import QuickStepsSection from "@/components/landing/QuickStepsSection";
import ScreenshotsSection from "@/components/landing/ScreenshotsSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import CTASection from "@/components/landing/CTASection";
import FAQSection from "@/components/landing/FAQSection";
import LandingFooter from "@/components/landing/LandingFooter";
import LandingChatbot from "@/components/public/LandingChatbot";

const NEXT_LANG = { es: "PT", pt: "BR", br: "EN", en: "ES" };
const LANG_CYCLE = { es: "pt", pt: "br", br: "en", en: "es" };

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState(() => detectLang());

  const t = T[lang] || T["pt"];

  // Update document title + meta description for SEO when language changes
  useEffect(() => {
    const titles = {
      es: "Kaisō System — Ecosistema operacional inteligente para food service | IA, CRM, POS, Delivery",
      pt: "Kaisō System — Ecossistema operacional inteligente para food service | IA, CRM, POS, Delivery",
      br: "Kaisō System — Ecossistema operacional inteligente para food service | IA, CRM, POS, Delivery",
      en: "Kaisō System — Intelligent operational ecosystem for food service | AI, CRM, POS, Delivery",
    };
    const descs = {
      es: "Transformamos restaurantes en operaciones inteligentes. IA, automatización, CRM, delivery y gestión en un único ecosistema. Sin comisiones.",
      pt: "Transformamos restaurantes em operações inteligentes. IA, automação, CRM, delivery e gestão num único ecossistema. Sem comissões.",
      br: "Transformamos restaurantes em operações inteligentes. IA, automação, CRM, delivery e gestão em um único ecossistema. Sem comissões.",
      en: "We transform restaurants into intelligent operations. AI, automation, CRM, delivery and management in one ecosystem. No commissions.",
    };
    document.title = titles[lang] || titles["pt"];
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", descs[lang] || descs["pt"]);
    const htmlEl = document.documentElement;
    const htmlLangMap = { es: "es", pt: "pt", br: "pt-BR", en: "en" };
    htmlEl.setAttribute("lang", htmlLangMap[lang] || "pt");
  }, [lang]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };
  const toggleLang = () => setLang(prev => LANG_CYCLE[prev] || "es");

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <LandingNav
        t={t}
        lang={lang}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        toggleLang={toggleLang}
        nextLangLabel={NEXT_LANG}
        scrollTo={scrollTo}
        navigate={navigate}
      />
      <HeroSection t={t} navigate={navigate} scrollTo={scrollTo} />
      <QuickStepsSection t={t} />
      <BenefitsSection t={t} />
      <HowItWorksSection t={t} />
      <FeaturesSection t={t} />
      <AISection t={t} />
      <PillarsSection t={t} />
      <ResultsSection t={t} />
      <IntegrationsSection t={t} />
      <AllInOneSection t={t} />
      <ScreenshotsSection t={t} navigate={navigate} />
      <PricingSection t={t} navigate={navigate} />
      <TestimonialsSection t={t} />
      <FAQSection t={t} />
      <CTASection t={t} navigate={navigate} />
      <LandingFooter t={t} lang={lang} scrollTo={scrollTo} />
      <LandingChatbot />
    </div>
  );
}
